/**
 * File utility functions for downloading files and extracting file extensions
 */

/**
 * Interface for download file parameters
 */
interface DownloadFileParams {
  /** Source URL or path of the file to download */
  src: string;
  /** Name to save the file as */
  filename: string;
}

/**
 * Downloads a file from a source URL and triggers a browser download
 * @param params - Object containing source URL and filename
 * @returns Promise that resolves when download is complete
 *
 * @example
 * ```typescript
 * await downloadFile({
 *   src: 'https://example.com/image.jpg',
 *   filename: 'downloaded-image.jpg'
 * });
 * ```
 */
export const downloadFile = async ({ src, filename }: DownloadFileParams): Promise<void> => {
  const fetchResponse = await fetch(src);
  const imageBlob = await fetchResponse.blob();

  const blobUrl = URL.createObjectURL(imageBlob);
  try {
    const anchorElement = document.createElement('a');
    anchorElement.href = blobUrl;
    anchorElement.download = filename;
    anchorElement.style.display = 'none';
    document.body.appendChild(anchorElement);
    anchorElement.click();
    document.body.removeChild(anchorElement);
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
};

/**
 * Extracts the file extension from a filename
 * @param fileName - The filename to extract extension from
 * @returns The file extension (without the dot) or empty string if no extension
 *
 * @example
 * ```typescript
 * getFileExtension('document.pdf');     // Returns 'pdf'
 * getFileExtension('image.jpg');        // Returns 'jpg'
 * getFileExtension('README');           // Returns ''
 * getFileExtension('config.env.local'); // Returns 'local'
 * ```
 */
export const getFileExtension = (fileName: string): string => {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1] : '';
};

export const isImageFileType = (fileType: string): boolean => {
  return fileType.startsWith('image/');
};
